/**
 * Wishlist functionality for Shopify theme
 * Handles adding/removing products from wishlist and displaying wishlist page
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    this.updateWishlistCount();
    this.bindEvents();
    this.updateWishlistButtons();
  }

  /**
   * Load wishlist from localStorage
   */
  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading wishlist:', error);
      return [];
    }
  }

  /**
   * Save wishlist to localStorage
   */
  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateWishlistCount();
      this.updateWishlistButtons();
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  /**
   * Add product to wishlist
   */
  addToWishlist(productId, productTitle, productHandle, productPrice, productImage) {
    const existingIndex = this.wishlist.findIndex(item => item.id === productId);
    
    if (existingIndex === -1) {
      const wishlistItem = {
        id: productId,
        title: productTitle,
        handle: productHandle,
        price: productPrice,
        image: productImage,
        addedAt: new Date().toISOString()
      };
      
      this.wishlist.push(wishlistItem);
      this.saveWishlist();
      this.showNotification(`${productTitle} added to wishlist!`, 'success');
      return true;
    } else {
      this.showNotification(`${productTitle} is already in your wishlist!`, 'info');
      return false;
    }
  }

  /**
   * Remove product from wishlist
   */
  removeFromWishlist(productId) {
    const index = this.wishlist.findIndex(item => item.id === productId);
    
    if (index !== -1) {
      const removedItem = this.wishlist.splice(index, 1)[0];
      this.saveWishlist();
      this.showNotification(`${removedItem.title} removed from wishlist!`, 'success');
      return true;
    }
    return false;
  }

  /**
   * Check if product is in wishlist
   */
  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId);
  }

  /**
   * Get wishlist items
   */
  getWishlist() {
    return [...this.wishlist];
  }

  /**
   * Update wishlist count in header
   */
  updateWishlistCount() {
    const countElement = document.getElementById('wishlist-count');
    if (countElement) {
      const count = this.wishlist.length;
      countElement.textContent = count;
      countElement.style.display = count > 0 ? 'flex' : 'none';
    }
  }

  /**
   * Update wishlist button states
   */
  updateWishlistButtons() {
    const wishlistButtons = document.querySelectorAll('.wishlist-btn');
    
    wishlistButtons.forEach(button => {
      const productId = button.dataset.productId;
      if (productId) {
        const isInWishlist = this.isInWishlist(productId);
        button.classList.toggle('active', isInWishlist);
        
        const heartIcon = button.querySelector('svg');
        if (heartIcon) {
          heartIcon.style.fill = isInWishlist ? '#e91e63' : 'none';
          heartIcon.style.stroke = isInWishlist ? '#e91e63' : 'currentColor';
        }
        
        // Update button text/title
        button.title = isInWishlist ? 'Remove from wishlist' : 'Add to wishlist';
      }
    });
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Header wishlist toggle
    const wishlistToggle = document.querySelector('.wishlist-toggle');
    if (wishlistToggle) {
      wishlistToggle.addEventListener('click', () => {
        this.openWishlistModal();
      });
    }

    // Wishlist buttons on product cards
    document.addEventListener('click', (e) => {
      if (e.target.closest('.wishlist-btn')) {
        e.preventDefault();
        e.stopPropagation();
        
        const button = e.target.closest('.wishlist-btn');
        const productId = button.dataset.productId;
        const productTitle = button.dataset.productTitle;
        const productHandle = button.dataset.productHandle;
        const productPrice = button.dataset.productPrice;
        const productImage = button.dataset.productImage;
        
        if (this.isInWishlist(productId)) {
          this.removeFromWishlist(productId);
        } else {
          this.addToWishlist(productId, productTitle, productHandle, productPrice, productImage);
        }
      }
    });
  }

  /**
   * Open wishlist modal
   */
  openWishlistModal() {
    let modal = document.getElementById('wishlist-modal');
    
    if (!modal) {
      modal = this.createWishlistModal();
      document.body.appendChild(modal);
    }
    
    this.renderWishlistModal(modal);
    modal.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Focus trap for accessibility
    setTimeout(() => {
      const firstFocusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus();
    }, 100);
  }

  /**
   * Create wishlist modal HTML structure
   */
  createWishlistModal() {
    const modal = document.createElement('div');
    modal.id = 'wishlist-modal';
    modal.className = 'wishlist-modal';
    
    modal.innerHTML = `
      <div class="wishlist-modal-overlay"></div>
      <div class="wishlist-modal-content">
        <div class="wishlist-modal-header">
          <h2>My Wishlist</h2>
          <button type="button" class="wishlist-modal-close" aria-label="Close wishlist">×</button>
        </div>
        <div class="wishlist-modal-body">
          <!-- Wishlist items will be rendered here -->
        </div>
      </div>
    `;
    
    // Add event listeners
    const closeBtn = modal.querySelector('.wishlist-modal-close');
    const overlay = modal.querySelector('.wishlist-modal-overlay');
    
    const closeModal = () => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    };
    
    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', closeModal);
    
    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.style.display === 'block') {
        closeModal();
      }
    });
    
    return modal;
  }

  /**
   * Render wishlist modal content
   */
  renderWishlistModal(modal) {
    const body = modal.querySelector('.wishlist-modal-body');
    
    if (this.wishlist.length === 0) {
      body.innerHTML = `
        <div class="wishlist-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <h3>Your wishlist is empty</h3>
          <p>Add some products to your wishlist to see them here!</p>
          <a href="/collections/all" class="btn-primary">Continue Shopping</a>
        </div>
      `;
    } else {
      const itemsHtml = this.wishlist.map(item => `
        <div class="wishlist-item" data-product-id="${item.id}">
          <div class="wishlist-item-image">
            ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
          </div>
          <div class="wishlist-item-details">
            <h4 class="wishlist-item-title">
              <a href="/products/${item.handle}">${item.title}</a>
            </h4>
            <div class="wishlist-item-price">${this.formatMoney(item.price)}</div>
            <div class="wishlist-item-actions">
              <a href="/products/${item.handle}" class="btn-secondary">View Product</a>
              <button type="button" class="wishlist-remove-btn" data-product-id="${item.id}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3,6 5,6 21,6"></polyline>
                  <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
                </svg>
                Remove
              </button>
            </div>
          </div>
        </div>
      `).join('');
      
      body.innerHTML = `
        <div class="wishlist-items">
          ${itemsHtml}
        </div>
        <div class="wishlist-footer">
          <p>Total items: ${this.wishlist.length}</p>
        </div>
      `;
      
      // Add remove button event listeners
      body.querySelectorAll('.wishlist-remove-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const productId = e.target.closest('.wishlist-remove-btn').dataset.productId;
          this.removeFromWishlist(productId);
          this.renderWishlistModal(modal);
        });
      });
    }
  }

  /**
   * Format money (simple implementation)
   */
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  /**
   * Show notification
   */
  showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `wishlist-notification ${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          ${type === 'success' ? '<polyline points="20,6 9,17 4,12"></polyline>' : '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>'}
        </svg>
        <span>${message}</span>
      </div>
    `;
    
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#28a745' : '#17a2b8'};
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      transform: translateX(100%);
      transition: transform 0.3s ease;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      max-width: 300px;
    `;
    
    const content = notification.querySelector('.notification-content');
    content.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
    `;
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 10);
    
    // Animate out and remove
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
}

// Initialize wishlist when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.wishlistManager = new WishlistManager();
});

// Make it globally available
window.WishlistManager = WishlistManager;